import { Client } from "@/Common/Api/Client";
import { ImportedPatientMappingResponse } from "@/Common/Api/ExternalCommunications/Dto/ImportedPatientMappingResponse";
import { SearchIndividualResult } from "@/Common/Api/Individuals/Dto/SearchIndividualResult";
import { ValidateIndividualDuplicateResponse } from "@/Common/Api/Individuals/Dto/ValidateIndividualDuplicateResponse";
import { FindPatientDuplicateRequest } from "@/Common/Api/Patient/Dto/FindPatientDuplicateRequest";
import { IImportablePatient } from "@/Common/Models/Patient/IPatient";
import { Person } from "@/Common/Models/Person/Person";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExternalPatientImportAdvancedSearchRenderer } from "./ExternalPatientImportAdvancedSearchRenderer";

abstract class PatientImportHandler {
    public abstract renderAdvancePatientSearch(foundPatient: Person, patientInfoForHighlight: IImportablePatient, className?: string, index?: number, onSelect?: (p: Person) => void): JSX.Element;

    public isImportable(importablePatient: IImportablePatient, existingPatientContactFacetId: string): boolean {
        return !!importablePatient && !!existingPatientContactFacetId;
    }

    public async getDuplicatePatients(healthCardinfo: FindPatientDuplicateRequest): Promise<SearchIndividualResult[]> {
        let response = await Client.postAsync<ValidateIndividualDuplicateResponse>(
            ServerOperations.Server.Myle_ValidateIndividualDuplicate,
            {
                HealthCardNumber: healthCardinfo.number,
                HealthCardRegionLookupId: healthCardinfo.regionLookupId,
                PatientOnly: true
            }
        );

        if (isArrayNullOrUndefinedOrEmpty(response.data.DuplicateIndividuals))
            return [];

        return response.data.DuplicateIndividuals;
    }
}

class ExternalCommunicationPatientImportHandler extends PatientImportHandler {
    private importedPatients: ImportedPatientMappingResponse[];

    constructor(importedPatients: ImportedPatientMappingResponse[]) {
        super();
        this.importedPatients = importedPatients;
    }

    public renderAdvancePatientSearch(foundPatient: Person, patientInfoForHighlight: IImportablePatient, className?: string, index?: number, onSelect?: (patient: Person) => void): JSX.Element {
        let isImportable = this.isImportable(patientInfoForHighlight, foundPatient?.ContactFacetId);
        return ExternalPatientImportAdvancedSearchRenderer.render(foundPatient, patientInfoForHighlight.toTransferable(), isImportable, className, index, onSelect);
    }

    public isImportable(linkToPatient: IImportablePatient, linkFromPatientContactFacetId: string): boolean {
        if (!super.isImportable(linkToPatient, linkFromPatientContactFacetId) || !this.importedPatients)
            return false;

        return !this.isPatientImported(linkFromPatientContactFacetId);
    }

    public async getDuplicatePatients(healthCardInfo: FindPatientDuplicateRequest): Promise<SearchIndividualResult[]> {
        let duplicatePatientIds = await super.getDuplicatePatients(healthCardInfo);

        return duplicatePatientIds.filter(duplicate => !this.isPatientImported(duplicate.ContactFacetId));
    }

    public static loadImportedPatientMapping(clientTag: string): Promise<ImportedPatientMappingResponse[]> {
        return new Promise((resolve, reject) => {
            Client.Postv2(
                ServerOperations.Server.Myle_ExternalCommunication_LoadImportedPatientMapping,
                { ClientTag: clientTag },
                response => resolve(response.Results || []),
                errorMsg => reject(errorMsg)
            );
        });
    }

    private isPatientImported(contactFacetId: string): boolean {
        return this.importedPatients.some(x => x.InternalPatientId === contactFacetId);
    }
}

export { ExternalCommunicationPatientImportHandler, PatientImportHandler };
